import { serialize } from "object-to-formdata";
import {
  LoginInput,
  Result,
  UserView,
  ProfileInput,
  ProfileImageInput,
  SignUpInput,
  UserRulesView,
  UserRulesInput,
  LogView,
  LogDetailsInput,
  LogAttachmentInput,
  LogAttachmentView,
  LogAttachment,
} from "../../models/general";

import { WeekScheduleInput, WeekScheduleView, UpdateCustomStatusInput, StatusHistory, LabeledFaceDescriptorsInput } from "../../models/attendance";
import request from "../../utils/request";

export async function getUserInfo() {
  const response = await request.get<Result<UserView>>("Account/GetUserInfo");
  return response.data;
}

export async function getUserInfoById(userId: string) {
  const response = await request.get<Result<UserView>>(`Account/${userId}`);
  return response.data;
}

export async function updateProfile(input: ProfileInput, id?: string) {
  const response = await request
    .post<Result<UserView>>(id ? `account/${id}` : "account", serialize(input));
  return response.data;
}

export async function updateProfileImage(input: ProfileImageInput, id?: string) {
  const response = await request
    .post<Result<UserView>>(id ? `account/UpdateProfileImage/${id}` : "account", serialize(input));
  return response.data;
}


export async function changeLocale(locale: string) {
  const response = await request.post<Result<boolean>>(`account/changeLocale/${locale}`);
  return response.data;
}

export async function getLocale() {
  const response = await request.get<Result<string>>("account/locale");
  return response.data;
}

export async function removeImage() {
  const response = await request.delete<Result<boolean>>("account/image");
  return response.data;
}

// Unified users fetch
// scope: Company|Department|Country|Branch (case sensitive to match enum on backend)
export async function FetchUsers(scope: string, id: number, archived: boolean = false) {
  const response = await request.get<Result<UserView[]>>(`account/Users`, { params: { scope, id, archived } });
  return response.data;
}

export async function getUser(id: string) {
  const response = await request.get<Result<UserView>>(`account/${id}`);
  return response.data;
}

export async function addUsers(users: SignUpInput[]) {
  const response = await request.post<Result<string[]>>("account/addUsers", users);
  return response.data;
}

// Admin password reset — mirrors AdminResetPasswordInput / AdminResetPasswordResult on the server.
export interface AdminResetPasswordInput {
  userId: string;
  newPassword?: string;          // omit/null/empty → server auto-generates
  mustChangeOnNextLogin: boolean;
  revokeActiveSessions: boolean;
}

export interface AdminResetPasswordResult {
  generatedPassword: string;
  mustChangeOnNextLogin: boolean;
  revokedSessionCount: number;
}

export async function resetUserPassword(input: AdminResetPasswordInput) {
  const response = await request.post<Result<AdminResetPasswordResult>>(
    "account/ResetUserPassword",
    input,
  );
  return response.data;
}

// Convenience wrapper: create a single user (still backed by the existing AddUsers endpoint).
export async function addUser(user: SignUpInput) {
  return addUsers([user]);
}

// Welcome-email management — mirrors WelcomeEmailStatus / SendWelcomeEmails on the server.
// Operator MUST pass companyId (targets a specific company); Hr/Admin must NOT pass it
// (the backend pins to their own company and rejects Operator calls that omit it).
export async function getWelcomeEmailStatus(companyId?: number) {
  const response = await request.get<Result<UserView[]>>(
    "account/WelcomeEmailStatus",
    companyId != null ? { params: { companyId } } : undefined,
  );
  return response.data;
}

// Per-user outcome for a welcome-email send. One bad id never aborts the rest — inspect each entry.
export interface SendWelcomeEmailResult {
  userId: string;
  success: boolean;
  message: string;
}

// Body is a RAW JSON array of user-id strings (not wrapped in an object).
export async function sendWelcomeEmails(userIds: string[]) {
  const response = await request.post<Result<SendWelcomeEmailResult[]>>(
    "account/SendWelcomeEmails",
    userIds,
  );
  return response.data;
}

export async function deleteUser(id: string) {
  const response = await request.delete<Result<{}>>(`account/${id}`, {
    params: { deleteMyAccount: false },
  });
  return response.data;
}

export async function restoreUser(id: string) {
  const response = await request.put<Result<{}>>(`account/${id}`);
  return response.data;
}

export async function getRoles() {
  const response = await request.get<Result<string[]>>(`account/roles`);
  return response.data;
}

export async function getUserManagers(userId: string) {
  const response = await request.get<Result<UserView[]>>(`account/GetUserManagers/${userId}`);
  return response.data;
}

export async function checkIsManager(userId: string) {
  const response = await request.get<Result<boolean>>(`account/CheckIsManager/${userId}`);
  return response.data;
}

export async function updateManagers(userId: string, managerIds: string[]) {
  const response = await request.post<Result<UserView>>(`account/UpdateManagers/${userId}`, {
    managerIds,
  });
  return response.data;
}

export async function getDictionary(locale: string) {
  const response = await request.get<Result<string[]>>(`account/dictionary/${locale}`);
  return response.data;
}

export async function UpdateCustomStatus(input: UpdateCustomStatusInput) {
  const response = await request.post<Result<UserView>>(`account/UpdateCustomStatus`, input);
  return response.data;
}

export async function getUserRules(userId: string) {
  const response = await request.get<Result<UserRulesView>>(`account/GetUserRules/${userId}`);
  return response.data;
}

export async function updateUserRules(userId: string, input: UserRulesInput) {
  const response = await request.post<Result<UserRulesView>>(`account/UpdateUserRules/${userId}`, input);
  return response.data;
}

export async function GetStatusHistory() {
  const response = await request.get<Result<StatusHistory[]>>(`account/GetStatusHistory`);
  return response.data;
}

export async function Log(details: LogDetailsInput) {
  // const response = await request.post<Result<Logview>>(`account/Log`,details);
  // return response.data;
  const formData = new FormData();
  formData.append('message', details.message.toString())
  formData.append('logType', details.logType.toString())
  for (let i = 0; i < details.attachments.length; i++)
    formData.append('attachments', details.attachments[i])
  const response = await request.post<Result<LogView>>(`account/Log`,
    formData);
  return response.data;

}

export async function AddLogAttachment(details: LogAttachmentInput) {
  // const response = await request.post<Result<LogAttachmentView[]>>(`account/AddLogAttachment`,details);
  // return response.data;
  const formData = new FormData();
  formData.append('logId', details.logId.toString())
  for (let i = 0; i < details.attachments.length; i++)
    formData.append('attachments', details.attachments[i])
  const response = await request.post<Result<LogAttachmentView[]>>(`account/AddLogAttachment`,
    formData);
  return response.data;
}

export async function setWeekSchedule(details: WeekScheduleInput) {
  const response = await request.post<Result<WeekScheduleView>>(`account/SetWeekSchedule`, details);
  return response.data;
}

export async function getWeekSchedule(userId: string) {
  const response = await request.get<Result<WeekScheduleView>>(`account/GetWeekSchedule/${userId}`);
  return response.data;
}

export async function getTimeSheetWeekSchedule(userId: string) {
  const response = await request.get<Result<WeekScheduleView>>(`account/GetTimeSheetWeekSchedule/${userId}`);
  return response.data;
}

export async function addLabeledFaceDescriptors(input: LabeledFaceDescriptorsInput) {
  const response = await request.post<Result<UserView>>(`account/AddLabeledFaceDescriptors`, input);
  return response.data;
}

export async function checkReEnrollmentRequired() {
  const response = await request.get<Result<{ requiresReEnrollment: boolean; hasDescriptor: boolean }>>('account/CheckReEnrollmentRequired');
  return response.data;
}

// Browser web-push (FCM). Store this browser's FCM web token so backend push
// delivery can reach it; pass an empty string to clear it (e.g. on sign-out).
// Uses the shared authenticated request client — no manual auth handling.
export async function updateWebPushToken(token: string) {
  const response = await request.post<Result<boolean>>(
    "Account/UpdateWebPushToken",
    { token },
  );
  return response.data;
}

export async function clearReEnrollmentFlag() {
  const response = await request.post<Result<boolean>>('account/ClearReEnrollmentFlag');
  return response.data;
}